// アプリケーションインポート機能

import type { ImportedApp, AppSection } from '../types/index.js';

// インストールコマンドのテンプレート（ファイル名から推測）
const installCommandTemplates: Record<string, string> = {
    'chrome': '/silent /install --system-level --do-not-launch-chrome',
    '7z': '/S',
    'git': '/VERYSILENT /NORESTART',
    'vscode': '/VERYSILENT /NORESTART /MERGETASKS=!runcode',
    'unity': '/allusers /S',
    'obs': '/S',
    'net': '/install /quiet /norestart',
    'default': '/S'
};

interface ParseResult {
    apps: ImportedApp[];
    installerPath: string;
}

/**
 * Treeコマンドの出力を解析
 * @param treeText - tree /F の出力テキスト
 * @returns 解析結果
 */
export function parseTreeOutput(treeText: string): ParseResult {
    const lines = treeText.split('\n');
    const apps: ImportedApp[] = [];
    let installerPath = 'installer'; // デフォルトパス
    let currentFolder = '';
    
    // installerフォルダを検出
    for (const line of lines) {
        // フォルダ行を検出
        const folderMatch = line.match(/(?:├──|└──|    )([^\\\r\n]+)/);
        if (folderMatch) {
            const folderName = folderMatch[1].trim();
            if (folderName.toLowerCase().includes('installer')) {
                installerPath = folderName;
                currentFolder = folderName;
                break;
            }
        }
    }
    
    // .exeファイルを検出
    for (const line of lines) {
        // ファイル行を検出（├── や │  などの記号を含む）
        const fileMatch = line.match(/(?:├──|│   |└──|    )([^\r\n]+\.exe)/i);
        if (fileMatch) {
            const fileName = fileMatch[1].trim();
            const appName = fileName.replace(/\.exe$/i, '').replace(/[-_]/g, ' ');
            
            // インストールコマンドを推測
            let installCmd = installCommandTemplates.default;
            const fileNameLower = fileName.toLowerCase();
            if (fileNameLower.includes('chrome')) {
                installCmd = installCommandTemplates.chrome;
            } else if (fileNameLower.includes('7z') || fileNameLower.includes('7-zip')) {
                installCmd = installCommandTemplates['7z'];
            } else if (fileNameLower.includes('git')) {
                installCmd = installCommandTemplates.git;
            } else if (fileNameLower.includes('vscode') || fileNameLower.includes('code')) {
                installCmd = installCommandTemplates.vscode;
            } else if (fileNameLower.includes('unity')) {
                installCmd = installCommandTemplates.unity;
            } else if (fileNameLower.includes('obs')) {
                installCmd = installCommandTemplates.obs;
            } else if (fileNameLower.includes('runtime') || fileNameLower.includes('net')) {
                installCmd = installCommandTemplates.net;
            }
            
            apps.push({
                id: 'app_' + fileName.replace(/[^a-zA-Z0-9]/g, '_'),
                name: appName,
                fileName: fileName,
                path: installerPath,
                installCmd: installCmd
            });
        }
    }
    
    return { apps, installerPath };
}

/**
 * アプリケーションセクションを生成
 * @param importedApps - インポートされたアプリケーションリスト
 * @returns アプリケーションセクション
 */
export function createAppSection(importedApps: ImportedApp[]): AppSection | null {
    if (importedApps.length === 0) return null;
    
    const items = importedApps.map(app => {
        return {
            id: app.id,
            title: app.name,
            description: `インストーラー: ${app.fileName}`,
            requiresAdmin: true,
            isAdminCheck: false,
            isAppInstall: true,
            appData: app,
            code: `echo ${app.name} をインストール中...\nif exist "%INSTALLER%\\${app.fileName}" (\n  "%INSTALLER%\\${app.fileName}" ${app.installCmd}\n  echo ${app.name} installed\n)\n`
        };
    });
    
    return {
        title: "アプリケーションインストール",
        sections: [{
            title: "アプリケーションインストール",
            items: items
        }]
    };
}

